import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { rx } from '@app/shared/rx/rx-wrapper';
import { E2E_ATTRIBUTES } from '@app/shared/types/enums/e2e-attributes.enum';
import { BaseComponent } from '../base-component/base.component';
import { IFilter, IFilterItem } from '../filter-panel/filter.types';

@Component({
  selector: 'filter-settings',
  templateUrl: './filter-settings.component.html',
  styleUrls: ['./filter-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterSettingsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input() public formCategory: FormGroup;
  @Input('filter') public set filterConfig(data: IFilter) {
    this.filter = data;
    this.selectedLabels = this.getSelectedLabels();
  }

  public isSlideShown = true;
  public isChildSlideShown = true;
  public checkBoxAll: FormControl;
  public filter: IFilter;
  public selectedLabels: string;
  public e2eAttr: string = E2E_ATTRIBUTES.FILTER_SETTINGS;
  public E2E_ATTRIBUTES: typeof E2E_ATTRIBUTES = E2E_ATTRIBUTES;

  public constructor(protected injector: Injector) {
    super(injector);
  }

  public ngOnInit() {
    this.checkBoxAll = new FormControl(false);
    this.formCategory.valueChanges
      .pipe(
        rx.startWith(this.formCategory.value),
        rx.takeUntil(this.cancelSubscription$),
      )
      .subscribe((data: Record<string, boolean>) => {
        this.checkAll(data);
        this.cd.markForCheck();
      });
  }

  public toggleChildSlide(): void {
    this.isChildSlideShown = !this.isChildSlideShown;
  }

  public toggleSlide(): void {
    this.isSlideShown = !this.isSlideShown;
  }

  public checkAll(data: Record<string, boolean>): void {
    let isTrue = true;

    Object.keys(data).forEach((control: string) => {
      if (control !== 'all' && !data[control]) {
        this.checkBoxAll.patchValue(false);
        isTrue = false;
      }
    });

    if (isTrue) {
      this.checkBoxAll.patchValue(true);
    }
  }

  public selectAll(status: boolean): void {
    const controls: Record<string, AbstractControl> =
      this.formCategory.controls;

    Object.keys(controls).forEach((control: string) => {
      controls[control].patchValue(status);
    });
  }

  public selectAllChildren(status: boolean, id: string): void {
    const ids: string[] = this.getFilterItemChildrenIds(id);
    const controls: Record<string, AbstractControl> =
      this.formCategory.controls;

    ids.forEach((idKey: string) => {
      controls[idKey].patchValue(status);
    });
  }

  public getSelectedLabels(): string {
    if (!this.filter?.items?.length) {
      return '';
    }

    const selected = this.filter.items.filter(
      (item: IFilterItem) => item.selected,
    );

    if (selected.length === this.filter.items.length) {
      return this.i18nService.instant(this.I18n.CommonStrings.All);
    }
    return selected
      .map((item: IFilterItem) => this.i18nService.instant(item.name()))
      .join(', ');
  }

  public trackByFilterItem(index: number, item: IFilterItem): IFilterItem {
    return item;
  }

  protected getFilterItemChildrenIds(itemId: string): string[] {
    const ids: string[] = [itemId];

    this.filter?.items?.forEach((item: IFilterItem) => {
      if (item.id === itemId && item.items) {
        item.items.forEach((childItem) => {
          if (childItem.id) {
            ids.push(childItem.id);
          }
        });
      }
    });

    return ids;
  }
}
